export type FontStyle = "normal" | "italic";
export type FontSubset = "latin" | "cyrillic" | "japanese";

export interface FontAsset {
  subset: FontSubset;
  url: string;
}

export interface FontVariant {
  weight: number;
  style: FontStyle;
  assets: FontAsset[];
}

export interface SelfHostedFont {
  id: string;
  family: string;
  variants: FontVariant[];
}

const constructorUrl = process.env.REACT_APP_CONSTRUCTOR_URL?.replace(/\/$/, "");
const fontAssetsBaseUrl = constructorUrl ? `${constructorUrl}/fonts` : "/fonts";

const subsetRanges: Record<FontSubset, string> = {
  latin:
    "U+0000-00FF,U+0131,U+0152-0153,U+02BB-02BC,U+02C6,U+02DA,U+02DC,U+0304,U+0308,U+0329,U+2000-206F,U+20AC,U+2122,U+2190,U+2191,U+2192,U+2193,U+2212,U+2215,U+FEFF,U+FFFD",
  cyrillic: "U+0301,U+0400-045F,U+0490-0491,U+04B0-04B1,U+2116",
  japanese:
    "U+3000-30FF,U+31F0-31FF,U+3200-4DBF,U+4E00-9FFF,U+F900-FAFF,U+FF00-FFEF",
};

const assetUrl = (fontId: string, subset: FontSubset, weight: number, style: FontStyle) =>
  `${fontAssetsBaseUrl}/${fontId}/${fontId}-${subset}-${weight}-${style}.woff2`;

const fontsWithItalic = new Set([
  "arimo",
  "comic-neue",
  "cousine",
  "fira-sans",
  "ibm-plex-sans",
  "inter",
  "jetbrains-mono",
  "libre-baskerville",
  "libre-franklin",
  "lora",
  "merriweather",
  "montserrat",
  "noto-sans",
  "noto-serif",
  "open-sans",
  "playfair-display",
  "pt-sans",
  "pt-serif",
  "roboto",
  "rubik",
  "tinos",
]);

const fontsWithCyrillic = new Set([
  "arimo",
  "bad-script",
  "caveat",
  "comfortaa",
  "cousine",
  "fira-code",
  "fira-sans",
  "ibm-plex-sans",
  "inter",
  "jetbrains-mono",
  "libre-franklin",
  "lobster",
  "lora",
  "manrope",
  "marck-script",
  "merriweather",
  "montserrat",
  "noto-sans",
  "noto-serif",
  "open-sans",
  "oswald",
  "pacifico",
  "playfair-display",
  "pt-sans",
  "pt-serif",
  "roboto",
  "rubik",
  "russo-one",
  "tinos",
  "unbounded",
]);

const fontsWithOnlyRegularWeight = new Set([
  "bad-script",
  "bebas-neue",
  "lobster",
  "marck-script",
  "pacifico",
  "russo-one",
]);

const createVariants = (
  fontId: string,
  weights: number[],
  styles: FontStyle[] = fontsWithItalic.has(fontId) ? ["normal", "italic"] : ["normal"],
  subsets: FontSubset[] = fontsWithCyrillic.has(fontId) ? ["latin", "cyrillic"] : ["latin"],
): FontVariant[] =>
  weights
    .filter((weight) => weight === 400 || !fontsWithOnlyRegularWeight.has(fontId))
    .flatMap((weight) =>
      styles.map((style) => ({
        weight,
        style,
        assets: subsets.map((subset) => ({
          subset,
          url: assetUrl(fontId, subset, weight, style),
        })),
      })),
    );

export const selfHostedFonts: SelfHostedFont[] = [
  { id: "inter", family: "Inter", variants: createVariants("inter", [400, 700]) },
  { id: "roboto", family: "Roboto", variants: createVariants("roboto", [400, 700]) },
  { id: "arimo", family: "Arimo", variants: createVariants("arimo", [400, 700]) },
  {
    id: "comic-neue",
    family: "Comic Neue",
    variants: createVariants("comic-neue", [400, 700], ["normal", "italic"], ["latin"]),
  },
  { id: "cousine", family: "Cousine", variants: createVariants("cousine", [400, 700]) },
  {
    id: "libre-franklin",
    family: "Libre Franklin",
    variants: createVariants("libre-franklin", [400, 700]),
  },
  { id: "lora", family: "Lora", variants: createVariants("lora", [400, 700]) },
  {
    id: "anton",
    family: "Anton",
    variants: createVariants("anton", [400], ["normal"], ["latin"]),
  },
  { id: "noto-sans", family: "Noto Sans", variants: createVariants("noto-sans", [400, 700]) },
  { id: "noto-serif", family: "Noto Serif", variants: createVariants("noto-serif", [400, 700]) },
  {
    id: "noto-sans-jp",
    family: "Noto Sans JP",
    variants: createVariants("noto-sans-jp", [400, 700], ["normal"], ["latin", "cyrillic", "japanese"]),
  },
  {
    id: "caveat",
    family: "Caveat",
    variants: createVariants("caveat", [400, 700], ["normal"], ["latin", "cyrillic"]),
  },
  { id: "tinos", family: "Tinos", variants: createVariants("tinos", [400, 700]) },
  ...[
    ["open-sans", "Open Sans"], ["montserrat", "Montserrat"], ["manrope", "Manrope"], ["rubik", "Rubik"], ["pt-sans", "PT Sans"], ["fira-sans", "Fira Sans"], ["ibm-plex-sans", "IBM Plex Sans"], ["merriweather", "Merriweather"], ["playfair-display", "Playfair Display"], ["pt-serif", "PT Serif"], ["libre-baskerville", "Libre Baskerville"], ["fira-code", "Fira Code"], ["jetbrains-mono", "JetBrains Mono"], ["oswald", "Oswald"], ["bebas-neue", "Bebas Neue"], ["russo-one", "Russo One"], ["unbounded", "Unbounded"], ["comfortaa", "Comfortaa"], ["marck-script", "Marck Script"], ["bad-script", "Bad Script"], ["pacifico", "Pacifico"], ["lobster", "Lobster"],
  ].map(([id, family]): SelfHostedFont => ({ id, family, variants: createVariants(id, [400, 700]) })),
];

export const legacyFontAliases: Record<string, string> = {
  Arial: "Arimo",
  "Arial Black": "Arimo",
  "Comic Sans MS": "Comic Neue",
  "Courier New": "Cousine",
  "Franklin Gothic Medium": "Libre Franklin",
  Georgia: "Lora",
  Impact: "Anton",
  "Lucida Console": "Cousine",
  "Lucida Sans Unicode": "Noto Sans",
  "Microsoft Sans Serif": "Arimo",
  "Palatino Linotype": "Lora",
  Sylfaen: "Noto Serif",
  Tahoma: "Arimo",
  Terminal: "Cousine",
  "Times New Roman": "Tinos",
  "Trebuchet MS": "Noto Sans",
  Verdana: "Noto Sans",
  Ebrima: "Noto Sans",
  "MS Gothic": "Noto Sans JP",
  "MV Boli": "Caveat",
  "Segoe Print": "Caveat",
  "Segoe UI Historic": "Noto Sans",
  Sitka: "Lora",
};

export const getPrimaryFontFamily = (family?: string): string | undefined => {
  if (!family) return undefined;
  return family.split(",")[0].trim().replace(/^['"]|['"]$/g, "");
};

export const resolveFontFamily = (family?: string): string | undefined => {
  const firstFamily = getPrimaryFontFamily(family);
  if (!firstFamily) return undefined;
  return legacyFontAliases[firstFamily] || firstFamily;
};

export const getSelfHostedFont = (family?: string): SelfHostedFont | undefined => {
  const resolvedFamily = resolveFontFamily(family);
  return selfHostedFonts.find((font) => font.family === resolvedFamily);
};

export const getSubsetUnicodeRange = (subset: FontSubset): string => subsetRanges[subset];

export const fontPickerOptions = selfHostedFonts.map(({ family }, index) => ({
  id: index + 1,
  font: family,
}));
